// eslint-disable-next-line @heidi/rules/no-new-legacy-session-data -- pre-existing patients delegate consuming the carved linked-sessions read (the rule anchors on the first import, hence the placement)
import {
  invalidateSessionsQuery,
  invalidateTeamMemberSessionsQuery,
} from '@/hooks/query/useQueryCombinedSessions';
import { patchMemberPatientProfile } from '@/pages/api/scribeAPIV2';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { toast } from '@heidi/design-system/toast';
// The mutation-key identity is frozen in `@heidi/patients/contracts`: the
// ownership ruling split this hook's two halves across two packages, and both
// halves must keep declaring the SAME key, from one site.
import {
  type PatientProfile,
  UPDATE_PATIENT_PROFILE,
  type UpdatePatientProfileRequest,
} from '@heidi/patients/contracts';
import { useMutateUpdateOwnPatientProfile as useMutateUpdateOwnPatientProfileCore } from '@heidi/patients/data';
import {
  GET_SESSION_BY_ID,
  GET_TEAM_MEMBER_SESSION_BY_ID,
  type QuerySessionResponse,
} from '@heidi/session/contracts';
import {
  isViewingOtherSessionAtom,
  useQueryLinkedSessionsByProfileId,
} from '@heidi/session/data';
import { useMutation } from '@tanstack/react-query';
import { GET_TEAM_MEMBER_PATIENT_PROFILES } from '../query/useQueryPatientProfiles';

import { logger } from '@/utils/logger/logger';
import {
  selectedReviewMemberAtom,
  useCombinedQueriesTeamGeneralInfo,
} from '@heidi/workspace/data';
import { useAtomValue } from 'jotai';
import { useRBACPermissionCheck } from '../useRBACPermissionCheck';

type Props = {
  patientProfileId: string;
  patientProfile: UpdatePatientProfileRequest;
  /** Passed as a query param for session direct-share permission check.
   *  When a session is directly shared, the sharee can process (create/update/link/unlink)
   *  patient profiles on that session by providing its ID here. */
  sessionId?: string;
};

export function useMutateUpdatePatientProfile(
  patientProfileId: string,
  options?: { showToast?: boolean }
) {
  const { isReviewingOtherUser } = useRBACPermissionCheck();
  const isViewingOtherSession = useAtomValue(isViewingOtherSessionAtom);
  const teamMemberMutation = useMutateUpdateTeamMemberPatientProfile(
    patientProfileId,
    options
  );
  const ownMutation = useMutateUpdateOwnPatientProfile(patientProfileId, options);

  if (isViewingOtherSession) {
    return ownMutation;
  }
  if (isReviewingOtherUser) {
    return teamMemberMutation;
  }
  return ownMutation;
}

/**
 * App-side delegate for `@heidi/patients/data` (D2.2, ARCFE-1053).
 *
 * The SESSION cache work is why this delegate is not a one-liner: the linked
 * sessions read, the sessions-list invalidation and the two optimistic
 * `GET_SESSION_BY_ID` patches are Session's cache, and app integration
 * sequences them (child design D2) rather than the package reaching across.
 * The `showToast` narrowing stays here too — it selects between a toast and a
 * caller's inline error surface, which is a UI decision.
 */
export function useMutateUpdateOwnPatientProfile(
  patientProfileId: string,
  options?: { showToast?: boolean }
) {
  const { data: linkedSessions } = useQueryLinkedSessionsByProfileId(patientProfileId);
  const isViewingOtherSession = useAtomValue(isViewingOtherSessionAtom);

  return useMutateUpdateOwnPatientProfileCore({
    isViewingOtherSession,
    onUpdated: (input) => {
      void invalidateSessionsQuery();

      if (linkedSessions?.sessions) {
        linkedSessions.sessions.forEach((linkedSession) => {
          reactQueryClient.setQueryData<QuerySessionResponse>(
            [GET_SESSION_BY_ID, { sessionId: linkedSession.session_id }],
            (oldData?: QuerySessionResponse) => {
              if (!oldData) {
                return undefined;
              }
              const newData: QuerySessionResponse = {
                ...oldData,
                patient_profile: {
                  ...oldData.patient_profile,
                  ...input.patientProfile,
                } as PatientProfile,
                session_name:
                  input.patientProfile.demographic_string || oldData.session_name,
              };
              return newData;
            }
          );
        });
      }

      // For direct-share sessions, the current session may not appear in
      // linkedSessions (which only contains the viewer's own sessions).
      // Directly patch the cache for the current session to update the UI.
      // Skip if the session was already patched in the linkedSessions loop above.
      if (
        isViewingOtherSession &&
        input.sessionId &&
        !linkedSessions?.sessions?.some((s) => s.session_id === input.sessionId)
      ) {
        reactQueryClient.setQueryData<QuerySessionResponse>(
          [GET_SESSION_BY_ID, { sessionId: input.sessionId }],
          (oldData?: QuerySessionResponse) => {
            if (!oldData) {
              return undefined;
            }
            return {
              ...oldData,
              patient_profile: {
                ...oldData.patient_profile,
                ...input.patientProfile,
              } as PatientProfile,
              session_name:
                input.patientProfile.demographic_string || oldData.session_name,
            };
          }
        );
      }

      const showToast = options?.showToast ?? true;
      if (showToast) {
        toast({
          tone: 'success',
          title: 'Patient details successfully updated',
        });
      }
    },
    onUpdateFailed: () => {
      // The `logger.error('Error:', error)` moved into the package with the
      // hook body. Surfaces are responsible for catching the rejection from
      // `mutateAsync` and rendering inline error UI when they pass
      // `showToast: false` (e.g. the patient drawer / page inline edits).
      // The global toast is only useful when there is no per-field error
      // surface to land on.
      if (options?.showToast !== false) {
        toast({
          tone: 'destructive',
          title: 'Backend Error',
        });
      }
    },
  });
}

export function useMutateUpdateTeamMemberPatientProfile(
  patientProfileId: string,
  options?: { showToast?: boolean }
) {
  const { data: linkedSessions } = useQueryLinkedSessionsByProfileId(patientProfileId);
  const { teamDetails } = useCombinedQueriesTeamGeneralInfo();
  const teamId = teamDetails?.team.id;
  const selectedReviewMember = useAtomValue(selectedReviewMemberAtom);

  return useMutation({
    mutationKey: [UPDATE_PATIENT_PROFILE],
    mutationFn: async (input: Props) => {
      if (!teamId || !selectedReviewMember) {
        throw new Error('Team ID or selected review member not found');
      }
      return await patchMemberPatientProfile(
        teamId,
        selectedReviewMember,
        input.patientProfileId,
        input.patientProfile
      );
    },
    onSuccess: (_, input) => {
      void invalidateTeamMemberSessionsQuery(teamId, selectedReviewMember);

      if (linkedSessions?.sessions) {
        linkedSessions.sessions.forEach((linkedSession) => {
          reactQueryClient.setQueryData<QuerySessionResponse>(
            [
              GET_TEAM_MEMBER_SESSION_BY_ID,
              { teamId },
              { memberId: selectedReviewMember },
              { sessionId: linkedSession.session_id },
            ],
            (oldData?: QuerySessionResponse) => {
              if (!oldData) {
                return undefined;
              }
              const newData: QuerySessionResponse = {
                ...oldData,
                patient_profile: {
                  ...oldData.patient_profile,
                  ...input.patientProfile,
                } as PatientProfile,
                session_name:
                  input.patientProfile.demographic_string || oldData.session_name,
              };
              return newData;
            }
          );
        });
      }

      void reactQueryClient.invalidateQueries({
        queryKey: [GET_TEAM_MEMBER_PATIENT_PROFILES, teamId, selectedReviewMember, true],
      });

      const showToast = options?.showToast ?? true;
      if (showToast) {
        toast({
          tone: 'success',
          title: 'Patient details successfully updated',
        });
      }
    },
    onError: (error) => {
      logger.error('Error:', error);
      if (options?.showToast !== false) {
        toast({
          tone: 'destructive',
          title: 'Backend Error',
        });
      }
    },
  });
}
